import { Feature, GeoJsonProperties, Geometry } from 'geojson';

import { DataSource, DataSourceId } from './data-source';
import { ScenarioId } from './scenario';
import { ValueOptionItem } from './value-option';

/**
 * A strongly-typed GeoDataset ID.
 */
export type GeoDatasetId = number;

export interface GeoDataset {
  id: GeoDatasetId;
  modelId: number;
  categoryId: number | null;
  iconId: number | null;
  /**
   * Gets the data view icon URI.
   */
  iconUri: string | null;
  name: string;
  description: string;
  properties?: GeoDatasetProperties;
  created: Date;
  updated: Date;
  sortOrder: number | null;
  geoDatasetDimensionFilters?: GeoDatasetDimensionFilter[] | null;
  geoDatasetExternalLayers?: GeoDatasetExternalLayer[] | null;
  geoDatasetInternalLayers?: GeoDatasetInternalLayer[] | null;
}

export interface GeoDatasetDimensionFilter {
  id: number;
  valueOptionId: number;
  name: string;
  columnName: string;
  filterOrder: number;
}

export interface GeoDatasetProperties {
  centerLat: number;
  centerLong: number;
  zoomFactor: number;
}

export type GeoDatasetPropertiesResult = {
  geoDatasetId: GeoDatasetId;
} & GeoDatasetProperties;

/**
 * A strongly-typed GeoDataset external layer ID.
 */
export type GeoDatasetExternalLayerId = number;

export interface GeoDatasetExternalLayer {
  id: GeoDatasetExternalLayerId;
  geoDataSetId: GeoDatasetId;
  name: string;
  connectionId: DataSourceId;
  connection: DataSource;
  tableName?: string;
  dboObjectType: GeoDatasetExternalLayerDBOObjectType;
  layerOrder: number;
  dataLayerType: number;
  layerSettings: GeoDatasetExternalLayerSettings;
  properties?: GeoDatasetExternalLayerProperties[];
  featureProperties: GeoDatasetExternalLayerFeatureProperty[];
  geoFeatureProperties?: GeoDatasetExternalLayerGeoFeatureProperties[];
  fileBlob: string | null;
  fileUri: string | null;
  minZoom?: number;
  maxZoom?: number;
  lineWidthMinPixels?: number;
  lineWidthMaxPixels?: number;
  enabled: boolean;
  active: boolean;
}

export interface GeoDatasetExternalLayerSettings {
  color: string;
  width: number;
  opacity: number;
  filled: boolean;
  excludeUnusedFeatures: boolean;
  extruded: boolean;
}

export interface GeoDatasetExternalLayerGeoFeatureProperties {
  [key: string]: string | number;
}

export interface GeoDatasetExternalLayerProperties {
  [key: string]: boolean;
}

export interface GeoDatasetInternalLayerProperties extends GeoDatasetExternalLayerProperties {}

export interface GeoDatasetExternalLayerFeatureProperty {
  featurePropertyName: string;
  configType: string;
  colourType: string;
  category: string;
  gradient: GeoDatasetExternalLayerFeatureGradient;
  discreteColours: GeoDatasetLayerDiscreteColour[];
  gradientCalculationType?: GeoDatasetLayerGradientCalculationType;
  minColumn?: string;
  maxColumn?: string;
  minWidthColumn?: string;
  maxWidthColumn?: string;
  minRefWidthColumn?: string;
  maxRefWidthColumn?: string;
  tableJoinColumn: string;
  tableInfoColumn: string;
  active: boolean;
}

export type GeoDatasetInternalLayerGeoFeaturesState = {
  layerId: number;
  isFullyFetched: boolean;
};

export interface GeoDatasetLayerDiscreteColour {
  colourId: number;
  legend: string;
  colour: string;
  enabled: boolean;
}

export interface GeoDatasetLayerIcon {
  iconId: number;
  iconName: string;
  enabled: boolean;
}

export type LayerLegend = {
  layerId: number;
  layerType: GeoDatasetLayerType;
  colourIds: number[];
  colours: string[];
  layerDataType?: 'border' | 'feature';
};

export type GeoDatasetExternalLayerFeatureGradient =
  | {
      isGradient: boolean;
      gradientType?: string | null;
      degrees: string | null;
      colors: GeoDatasetExternalFeatureGradientColour[];
    }
  | undefined;

export type GeoDatasetInternalLayerGradient = GeoDatasetExternalLayerFeatureGradient;

export type LayerGeoJsonData = {
  type: string;
  features: {
    type: string;
    properties: Record<string, any>;
  }[];
};

export type RGBA = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type GeoDatasetExternalFeatureGradientColour = {
  value: string;
  left: string;
};

export type GeoDatasetExternalLayerJsonData = {
  id: number;
  name: string;
  data: string;
};

export type BlobStorageItem = {
  blobName: string;
  blobUrl: string;
};

export type BlobItemContent = {
  name: string;
  content: string;
};

type PolyProperties = { tooltip: string; layerType: string };

export type LayerCoordinates = [longitude: number, latitude: number];

export type Polyline = {
  name: string;
  colour: string;
  width: number;
  properties: PolyProperties;
  path: LayerCoordinates[];
};

export type Polygon = {
  name: string;
  lineColour: string;
  fillColour: string;
  width: number;
  properties: PolyProperties;
  contour: LayerCoordinates[];
};

export type Circle = {
  name: string;
  fillColor: string;
  sourceColor: string;
  radius: number;
  properties: {
    tooltip: string;
    layerType: string;
  };
  coordinates: LayerCoordinates;
};

export type LineBasedLayerFeatureProperties = {
  scalerank: number;
  from: [number, number];
  to: [number, number];
  fillColor: string;
  sourceColor: string;
  targetColor: string;
  width: number;
  tooltip?: string;
  layerType: GeoDatasetLayerType;
};

export type LineBasedLayerFeature = {
  type: 'Feature';
  properties: LineBasedLayerFeatureProperties;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
};

export type LineBasedLayerFeatureCollection = {
  type: 'FeatureCollection';
  features: LineBasedLayerFeature[];
};

export type FeatureCollection = {
  type: 'FeatureCollection';
  features: FeatureCollectionFeature[];
};

export type FeatureCollectionParsed = {
  type: 'FeatureCollection';
  features: Feature<Geometry, GeoJsonProperties>[];
};

export type IconLayerData = {
  coordinates: number[];
  iconSize: number;
  iconId: number;
  properties: { [name: string]: any } | null;
};

export type HeatmapLayerData = {
  coordinates: number[];
  weight: number;
  radius: number;
  properties: { [name: string]: any } | null;
};

export type FeatureCollectionFeature = {
  geoJsonData: string;
  properties: {
    fillColour?: string;
    sourceColour?: string;
    targetColour?: string;
    width: number;
    weight: number;
    radius: number;
    iconSize: number;
    iconId: number;
    tooltips: string;
  };
};

export type MapGeographyValues = {
  id: number;
  fillColour?: number | null;
  sourceColour?: number | null;
  targetColour?: number | null;
  width?: number | null;
  tooltip?: string | null;
  geoJson: string;
};

export type GeoDatasetInternalLayersGeoCoordinatesPayload = {
  scenarioName: string;
  internalLayerIds: GeoDatasetInternalLayerId[];
  latitude1: number;
  longitude1: number;
  latitude2: number;
  longitude2: number;
  jsonFilter?: string | null;
};

export interface GeoDatasetValueOptionDynamicValues {
  geoDatsetId: GeoDatasetId;
  valueOptionId: number;
  dynamicValues: ValueOptionItem[];
}

export type GetGeoDatasetValueOptionsDynamicValuesPayload = {
  scenarioId: ScenarioId;
  jsonFilter?: string;
};

/**
 * A strongly-typed GeoDataset internal layer ID.
 */
export type GeoDatasetInternalLayerId = number;

export interface GeoDatasetDataLayerType {
  id: GeoDatasetId;
  name: string;
}

export interface GeoDatasetDataLayerBase {
  colour: string;
  discreteColours: GeoDatasetLayerDiscreteColour[];
  width: string;
  opacity: number;
  category: string;
  gradientCategory: string;
  gradientCalculationType?: GeoDatasetLayerGradientCalculationType;
  gradient?: GeoDatasetExternalLayerFeatureGradient;
  minColumn?: string;
  maxColumn?: string;
  visible: boolean;
}

export interface GeoDatasetInternalLayer {
  id: GeoDatasetInternalLayerId;
  geoDataSetId: GeoDatasetId;
  name: string;
  connectionId: DataSourceId;
  connection: DataSource;
  tableName: string;
  dataLayerTypeId: number;
  properties?: GeoDatasetInternalLayerProperties[];
  layerSettings: GeoDatasetInternalLayerSettings;
  layerOrder: number;
  noOfDecimals?: number;
  maxRecordsToGet?: number;
  minZoom?: number;
  maxZoom?: number;
  widthMinPixels?: number;
  widthMaxPixels?: number;
  minColour?: number;
  maxColour?: number;
  featureCollection: FeatureCollection;
  useGradientColours: boolean;
  isTotalLayer: boolean;
  enabled: boolean;
  active: boolean;
}

export type GeoDatasetInternalLayerSettings =
  | GeoDatasetDataLayerLine
  | GeoDatasetDataLayerArc
  | GeoDatasetDataLayerCircle
  | GeoDatasetDataLayerPolyline
  | GeoDatasetDataLayerPolygon
  | GeoDatasetDataLayerIcon
  | GeoDatasetDataLayerHeatmap;

export interface GeoDatasetDataLayerCircle extends GeoDatasetDataLayerBase {
  filled: boolean;
  scaleZoom: boolean;
  radiusScaleFactor: number;
  fillColor: string;
}

export interface GeoDatasetDataLayerIcon extends GeoDatasetDataLayerBase {
  iconSize: string;
  iconId: string;
  icons: GeoDatasetLayerIcon[];
}

export interface GeoDatasetDataLayerHeatmap extends GeoDatasetDataLayerBase {
  colourRange: string;
  weight: string;
  radius: number;
  aggregationType: GeoDatasetHeatmapLayerAggregationType;
}

export interface GeoDatasetDataLayerArc extends GeoDatasetDataLayerBase {
  sourceColor: string;
  targetColor: string;
}

export interface GeoDatasetDataLayerLine extends GeoDatasetDataLayerArc {
  lineColor: string;
}

export interface GeoDatasetDataLayerPolyline extends GeoDatasetDataLayerBase {
  sourceColor: string;
}

export interface GeoDatasetDataLayerPolygon extends GeoDatasetDataLayerPolyline {
  fillColor: string;
}

export interface GeoDatasetColour {
  applicationId: number;
  colourId: number;
  colour: string;
}

/**
 * Request payload used to upload a geo dataset external layer file.
 */
export type UploadGeoDatasetExternalLayerFilePayload = {
  file: File;
  fileName: string;
  fileType: 0 | 1;
};

/**
 * Request payload used to create a geo dataset.
 */
export type CreateGeoDatasetPayload = Omit<GeoDataset, 'id' | 'iconUri' | 'created' | 'updated'>;

export interface GeoDatasetInternalLayersGeoCoordinates {
  internalLayerID: number;
  maxRecordsToGet: number;
  featureCollection: FeatureCollection;
}

/**
 * Request payload used to update an existing data view.
 */
export type UpdateGeoDatasetPayload = CreateGeoDatasetPayload;

export type UseUpdateGeoDatasetPayload = UpdateGeoDatasetPayload & {
  geoDatasetId: GeoDatasetId;
};

export enum GeoDatasetInternalLayerType {
  Line = 1,
  Arc = 2,
  Circle = 3,
  Polyline = 4,
  Polygon = 5,
  Icon = 6,
  Heatmap = 7,
}

export enum GeoDatasetExternalLayerFeaturePropertyConfigType {
  AreaColour = 'Area Colour',
  LineThickness = 'Line Thickness',
  LineColour = 'Line Colour',
}

export enum GeoDatasetExternalLayerFeaturePropertyColourType {
  DiscreteColour = 'Discrete Colour',
  GradientColour = 'Gradient Colour',
  RangeWidth = 'Range Width',
}

export enum GeoDatasetLayerType {
  ExternalLayer = 'ExternalLayer',
  InternalLayer = 'InternalLayer',
}

/**
 * The request payload used to update a list of existing geodatasets.
 */
export interface UpdateGeoDatasetsPartialPayload {
  geoDatasets: GeoDatasetPartial[];
}

/**
 * Partial interface to make patch updates to a geodataset
 */
export interface GeoDatasetPartial extends Partial<GeoDataset> {
  id: GeoDatasetId;
}

export enum GeoDatasetExternalLayerDBOObjectType {
  TableOrView = 1,
  Function = 2,
}

export enum GeoDatasetHeatmapLayerAggregationType {
  Sum = 'SUM',
  Mean = 'MEAN',
}

export enum GeoDatasetLayerGradientCalculationType {
  Linear = 'Linear',
  Logarithmic = 'Logarithmic',
}
